'use client';

import { useSession } from '@shared/auth';
import { Button, FormField } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { usePlayerPermissions, usePlayerVerification, useSeonSession } from '@shared/hooks';
import { Spinner } from '@shared/icons';
import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldMessage } from '../../../../form-controls/FieldMessage/FieldMessage';
import { useMultistepForm } from '../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { TextInput } from '../../../../form-controls/TextInput/TextInput';
import { useKycDialog } from '../../../../global-dialogs/components/KycDialog/context/KycDialogContext';
import { PaymentMethodSelector } from '../../../../PaymentMethodSelector/PaymentMethodSelector';
import type { MethodOrInstrument } from '../../../../PaymentMethodSelector/types';
import { StatusNotification } from '../../../../StatusNotification/StatusNotification';
import { useCreatePayout } from '../hooks/useCreatePayout';
import type { CreatePayoutRequest } from '../types';
import type { WithdrawalFormData } from '../WithdrawSheet';

type WithdrawAmountStepProps = {
  namespace: string;
  withdrawableBalance: number;
  onError: (error: string) => void;
  onMount?: () => void;
  setTrackingDetails?: (details: { method?: string; amount?: string; provider?: string }) => void;
  'data-track-container'?: string;
};

function WithdrawAmountStep({
  namespace,
  withdrawableBalance,
  onError,
  onMount,
  setTrackingDetails,
  'data-track-container': dataTrackContainer,
  ...props
}: WithdrawAmountStepProps) {
  const t = useTranslations(namespace);
  const tGeneral = useTranslations('general');
  const formatCurrency = useCurrencyFormatter();
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
    clearErrors,
  } = useFormContext<WithdrawalFormData>();
  const { skipToStep, setStatus, goToNextStep } = useMultistepForm();
  const [isLoading, setIsLoading] = useState(false);
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const debug = createDebug({ isEnabled: false });
  const { payOutLocked } = usePlayerPermissions();

  const createPayoutMutation = useCreatePayout();

  const amount = watch('amountStep.amount');
  const selectedPaymentMethod = watch('amountStep.paymentMethod') || '';
  const [selectedPaymentMethodOrInstrument, setSelectedPaymentMethodOrInstrument] = useState<MethodOrInstrument | null>(null);

  const { getSeonSessionId } = useSeonSession();

  const handleConfirm = async () => {
    const isValid = await trigger('amountStep');
    if (!isValid) return;

    if (!selectedPaymentMethodOrInstrument) {
      return;
    }

    // If it's a payment method (not an instrument), go to next step
    if (!selectedPaymentMethodOrInstrument.apiReference) {
      goToNextStep();
      return;
    } else {
      await handleInstrumentWithdrawal();
    }
  };

  const handleInstrumentWithdrawal = async () => {
    if (!selectedPaymentMethodOrInstrument?.apiReference) {
      return;
    }

    setIsLoading(true);

    try {
      const seonSessionId = await getSeonSessionId();
      const payoutData: CreatePayoutRequest = {
        apiReference: selectedPaymentMethodOrInstrument.apiReference,
        amount: Number(amount) * 100, // Already converted to pennies
        documentId: playerId || '',
        ...(seonSessionId && { seonSessionId }),
      };

      await createPayoutMutation.mutateAsync(payoutData);
      setIsLoading(false);

      const provider = (selectedPaymentMethodOrInstrument as { apiName?: string }).apiName ?? selectedPaymentMethodOrInstrument.name;
      setTrackingDetails?.({
        method: selectedPaymentMethodOrInstrument.name,
        amount,
        provider,
      });

      if (setStatus) {
        setStatus('success');
      }
      if (skipToStep) {
        skipToStep('success');
      }
    } catch (error) {
      setIsLoading(false);
      debug.forceError('Withdrawal flow error:', error);

      // Navigate to error step
      const errorObj = error as Record<string, unknown>;
      const errorMessage = (errorObj?.message as string) || t('errors.unexpected');

      onError(errorMessage);

      if (setStatus) {
        setStatus('error');
      }
      if (skipToStep) {
        skipToStep('error');
      }
    }
  };

  const handlePaymentMethodChange = useCallback(
    (methodOrInstrument: MethodOrInstrument) => {
      setSelectedPaymentMethodOrInstrument(methodOrInstrument);
      const identifier = methodOrInstrument.apiReference ?? methodOrInstrument.name;
      setValue('amountStep.paymentMethod', identifier);
    },
    [setValue]
  );

  useEffect(() => {
    onMount?.();
  }, [onMount]);

  const getWithdrawButtonTrackingProps = () => {
    if (!selectedPaymentMethodOrInstrument) return {};
    const method = selectedPaymentMethodOrInstrument.name;
    const provider = selectedPaymentMethodOrInstrument.apiReference
      ? ((selectedPaymentMethodOrInstrument as { apiName?: string; 'data-track-provider'?: string })['data-track-provider'] ??
        (selectedPaymentMethodOrInstrument as { apiName?: string }).apiName ??
        method)
      : 'Bank Transfer';
    return {
      'data-track-name': 'Withdraw',
      'data-track-method': method,
      'data-track-provider': provider,
      'data-track-amount': amount || undefined,
    };
  };

  const { openKycDialog } = useKycDialog();

  const { data: playerVerification } = usePlayerVerification();
  const verifications = playerVerification?.verifications ?? [];

  const identityVerification = verifications.find(verification => verification.type === 'IDENTITY');
  const identityVerificationFailedStatuses = ['FAILED', 'FAILED_TRY_AGAIN'];
  const hasIdentityVerificationFailed = playerVerification != null && (!identityVerification || identityVerificationFailedStatuses.includes(identityVerification.status));

  const payoutAllowedStatuses = ['VERIFIED', 'VERIFIED_DOCUMENTED', 'NOT_REQUIRED', 'REVIEW'];
  const isPayoutWebComponentContentHidden = playerVerification != null && verifications.some(({ type, status }) => type === 'IDENTITY' && !payoutAllowedStatuses.includes(status));

  const isPayOutLockedWithdraw = !hasIdentityVerificationFailed && (payOutLocked || isPayoutWebComponentContentHidden);

  const openLopettoChat = () => {
    window.open(ROUTES.HELP_CENTRE.CHAT, '_blank');
  };

  return (
    <div className="flex flex-col gap-8" data-track-container={dataTrackContainer} data-track-withdrawable={(withdrawableBalance / 100).toString()} {...props}>
      {hasIdentityVerificationFailed && (
        <StatusNotification
          data-testid="identity-failed-notification"
          buttonTestId="identity-failed-notification-button"
          className="sm:flex-col sm:items-end"
          type="warning"
          label={t('identity_failed_label')}
          description={t('identity_failed_description')}
          buttonText={t('identity_failed_button')}
          onClick={openKycDialog}
          dataTrackType="Documents Required"
          dataTrackMessage="Documents - Upload"
          buttonTrackName="Upload"
        />
      )}
      {isPayOutLockedWithdraw && (
        <StatusNotification
          data-testid="payout-lock-notification"
          className="sm:flex-col sm:items-end"
          type="warning"
          label={t('payout_lock_label')}
          description={t('payout_lock_description')}
          buttonText={t('payout_lock_button')}
          onClick={openLopettoChat}
          dataTrackType="Documents Required"
          dataTrackMessage="Documents - Contact Support"
          buttonTrackName="Help"
        />
      )}
      <PaymentMethodSelector namespace={namespace} type="withdraw" value={selectedPaymentMethod} onChange={handlePaymentMethodChange} />
      <FieldMessage id="paymentMethod-error" message={errors.amountStep?.paymentMethod?.message as string} messageType="error" />

      <div className="bg-container p-4 rounded-lg flex justify-between items-center">
        <span className="paragraph-explanatory">{t('withdrawable_balance')}</span>
        <span className="paragraph-explanatory">{formatCurrency(withdrawableBalance).format(NumberFormats.WITH_DECIMALS).get()}</span>
      </div>

      <FormField
        name="amountStep.amount"
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id="amountStep.amount"
            disabled={isLoading}
            type="number"
            min="0"
            step="0.01"
            leftIcon={tGeneral('currency_symbol')}
            label={t('amount.label')}
            placeholder={t('amount.placeholder')}
            helpMessage={fieldState.error?.message}
            helpMessageType={fieldState.error ? 'error' : undefined}
            data-testid="amount-input"
            onKeyDown={e => {
              if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                e.preventDefault();
              }
            }}
            onChange={e => {
              const value = e.target.value;
              if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                field.onChange(e);
                // Clear any existing validation errors for this field when user types
                if (errors.amountStep?.amount) {
                  clearErrors('amountStep.amount');
                }
              }
            }}
            inputTrackingProps={{
              'data-track-name': 'Amount',
            }}
            helperTrackingProps={{
              'data-track-helper': 'Amount',
            }}
          />
        )}
      />

      <div data-track-container="Actions">
        <Button
          onClick={handleConfirm}
          disabled={isLoading || !amount || Number(amount) <= 0 || !!errors.amountStep?.amount || hasIdentityVerificationFailed || isPayOutLockedWithdraw}
          className="w-full rounded-full btn-label h-12"
          data-testid="withdraw-submit-button"
          {...getWithdrawButtonTrackingProps()}
        >
          {isLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : t('withdraw_button')}
        </Button>
      </div>
    </div>
  );
}

export { WithdrawAmountStep };
